import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AdunitsState,
  AdunitUpdateProps,
  InitialDataGetProps,
  InstanceGetDelProps,
} from '../../types/ReduxTypes';
import STATUS from '../../enums/statusEnum';

import { dbGet, dbPatch } from '../../utils/api';

import {
  ADS_API_FIRST_SETUP,
  ADS_API_ADUNITS_URL,
  DEFAULT_HEIGHT_PX,
  DEFAULT_WIDTH_PX,
} from '../../constansts';

import { RootState } from '..';

export const initialState: AdunitsState = {
  adunitsList: [],
  selectedAdUnitByCompId: [],
  loadingStatus: 'idle',
  instance: {},
  statusConnect: STATUS.disconnected,
  isMigrated: false,
  userName: '',
  loadingUpdateStatus: 'idle',
  dimensions: {
    width: DEFAULT_WIDTH_PX,
    height: DEFAULT_HEIGHT_PX,
  },
};

// Async
export const getInitialData = createAsyncThunk(
  'first_setup/getInitialData',
  async (payload: InitialDataGetProps, { rejectWithValue }) => {
    const { instance, compId } = payload;
    try {
      const url = `${ADS_API_FIRST_SETUP}?comp_id=${compId}&instance=${instance}`;
      const response = await dbGet({ url, instance });
      return response?.data || rejectWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const getAdUnitByCompId = createAsyncThunk(
  'adunits/getAdUnitByCompId',
  async (payload: InitialDataGetProps, { rejectWithValue }) => {
    const { instance, compId } = payload;

    try {
      const url = `${ADS_API_ADUNITS_URL}/bcid?comp_id=${compId}`;
      const response = await dbGet({ url, instance });
      return response?.data || rejectWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const updateAdUnitAsync = createAsyncThunk(
  'adunits/updateAdUnitAsync',
  async (payload: AdunitUpdateProps, { rejectWithValue }) => {
    try {
      const { instance, compId, dataAdSlot, body } = payload;
      const url = `${ADS_API_ADUNITS_URL}/connect/${dataAdSlot}?comp_id=${compId}`;
      const response = await dbPatch({ url, instance, body, method: 'patch' });
      return response?.data || rejectWithValue(response);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const disconnectAllAdsByCompIdAsync = createAsyncThunk(
  'adunits/disconnectAllAdsByCompIdAsync',
  async (payload: InstanceGetDelProps, { rejectWithValue }) => {
    const { instance, compId } = payload;
    const url = `${ADS_API_ADUNITS_URL}/bcid?comp_id=${compId}`;
    try {
      const response = await dbPatch({ url, instance, method: 'patch' });
      return response?.data || rejectWithValue(response);
    } catch (error) {
      rejectWithValue(error);
    }
  },
);
// Reducers
export const adunitsSlice = createSlice({
  name: 'adunits',
  initialState,
  reducers: {
    setDimensions: (
      state,
      action: PayloadAction<{ width: number; height: number }>,
    ) => {
      state.dimensions = {
        ...state.dimensions,
        ...action.payload,
      };
      console.log('redux dimensions', state.dimensions, action.payload);
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getInitialData.fulfilled, (state, action) => {
        console.log('first setup', action.payload);
        state.selectedAdUnitByCompId = Array.isArray(action.payload.selected_ad)
          ? action.payload.selected_ad[0]
          : null;
        state.adunitsList = action.payload.adunits?.length
          ? action.payload.adunits.sort((a: any, b: any): number => {
              const dateA = new Date(a.created_at);
              const dateB = new Date(b.created_at);
              return dateB.getTime() - dateA.getTime();
            })
          : [];
        state.instance = action.payload.instance;
        state.statusConnect = action.payload?.instance?.status_connect;
        state.isMigrated = action.payload?.instance?.is_migrated;
        state.userName = action.payload?.instance?.user_name;
        state.loadingStatus = 'succeeded';
      })
      .addCase(getInitialData.rejected, (state, action) => {
        state.statusConnect = STATUS.disconnected;
        state.adunitsList = [];
        state.selectedAdUnitByCompId = {};
        state.loadingStatus = 'failed';
      })
      .addCase(getAdUnitByCompId.fulfilled, (state, action) => {
        state.statusConnect = STATUS.connected;
        state.adunitsList = [];
        [state.selectedAdUnitByCompId] = action.payload;
        state.loadingStatus = 'succeeded';
      })
      .addCase(getAdUnitByCompId.rejected, (state, action) => {
        state.statusConnect = STATUS.disconnected;
        state.adunitsList = [];
        state.selectedAdUnitByCompId = {};
        state.loadingStatus = 'failed';
      })
      .addCase(updateAdUnitAsync.pending, (state, action) => {
        // state.loadingStatus = 'pending';
        state.loadingUpdateStatus = 'pending';
      })
      .addCase(updateAdUnitAsync.fulfilled, (state, action) => {
        state.loadingUpdateStatus = 'succeeded';
        // state.loadingStatus = 'succeeded';
        [state.selectedAdUnitByCompId] = action.payload;
      })
      .addCase(updateAdUnitAsync.rejected, (state, action) => {
        state.loadingUpdateStatus = 'failed';
        state.selectedAdUnitByCompId = [];
      });
  },
});

// Selectors
export const selectAdunitsList = (state: RootState) =>
  state.adunits.adunitsList;
export const selectStatusConnect = (state: RootState) =>
  state.adunits.statusConnect;
export const selectIsMigrated = (state: RootState) => state.adunits.isMigrated;
export const selectAdUnitByCompId = (state: RootState) =>
  state.adunits.selectedAdUnitByCompId;
export const selectInstance = (state: RootState) => state.adunits.instance;
export const selectLoadingStatus = (state: RootState) =>
  state.adunits.loadingStatus;
export const selectLoadingUpdateStatus = (state: RootState) =>
  state.adunits.loadingUpdateStatus;
export const selectUserName = (state: RootState) => state.adunits.userName;
export const selectAdUnitByCompIdDimensions = (state: RootState) =>
  state.adunits.selectedAdUnitByCompId.dimensions;
export const selectDimensions = (state: RootState) => state.adunits.dimensions;

export const { setDimensions } = adunitsSlice.actions;
export default adunitsSlice.reducer;
